// Globalna warstwa cache + dedup dla zapytań GET (axios).
//
// Po co: backend jest na Railway, baza na AWS — każde zbędne zapytanie kosztuje
// latencję. Te same ciężkie listy referencyjne (kierowcy, subcompanies, firmy,
// auta) były pobierane od nowa na każdej stronie i wielokrotnie przez widgety.
//
// Dwie ochrony:
//  1. In-flight dedup (WSZYSTKIE GET) — jeśli identyczny GET jest już w locie,
//     drugi woła ten sam promise zamiast osobnego requestu. Zero ryzyka
//     nieaktualności (to ten sam moment).
//  2. Krótki cache odpowiedzi (TYLKO listy referencyjne, TTL 60 s) — te dane
//     zmieniają się rzadko, a backend i tak ma własny cache z inwalidacją.
//
// Każda mutacja (POST/PUT/PATCH/DELETE) oraz logout czyści cały cache GET.
// Wzorowane na froncie który już działa w components/dashboard-widgets/_widgetMixin.js.

import axios from 'axios';

const REFERENCE_TTL_MS = 60 * 1000;

// Fragmenty URL identyfikujące listy referencyjne (rzadko zmienne, ciężkie).
const REFERENCE_MATCHERS = ['/drivers', 'sub-company', '/company', '/cars', '/fleet'];

const responseCache = new Map(); // key -> { ts, response }
const inflight = new Map(); // key -> Promise<response>

function isReference(url) {
  if (!url) return false;
  return REFERENCE_MATCHERS.some((m) => url.includes(m));
}

function keyOf(config) {
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${config.url}|${params}`;
}

export function clearHttpCache() {
  responseCache.clear();
  inflight.clear();
}

export function installHttpCache(instance = axios) {
  // W axios v1 defaults.adapter to tablica nazw (["xhr","http","fetch"]) —
  // getAdapter rozwiązuje ją do realnej funkcji adaptera.
  const getAdapter = instance.getAdapter || axios.getAdapter;
  const baseAdapter = getAdapter(instance.defaults.adapter);

  instance.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' || config.__noCache) return config;

    const key = keyOf(config);
    const cacheable = isReference(config.url);

    config.adapter = (cfg) => {
      // 1) świeży cache (tylko listy referencyjne)
      if (cacheable) {
        const hit = responseCache.get(key);
        if (hit && Date.now() - hit.ts < REFERENCE_TTL_MS) {
          return Promise.resolve({ ...hit.response, config: cfg, __fromCache: true });
        }
      }
      // 2) dedup równoległych identycznych GET-ów
      if (inflight.has(key)) return inflight.get(key);

      const p = baseAdapter(cfg)
        .then((response) => {
          if (cacheable) responseCache.set(key, { ts: Date.now(), response });
          inflight.delete(key);
          return response;
        })
        .catch((err) => {
          inflight.delete(key);
          throw err;
        });
      inflight.set(key, p);
      return p;
    };
    return config;
  });

  // Każdy udany zapis unieważnia cały cache GET (prosto i bezpiecznie —
  // backend ma własną, dokładniejszą inwalidację per-domena).
  instance.interceptors.response.use((response) => {
    const method = (response.config?.method || 'get').toLowerCase();
    if (method !== 'get') clearHttpCache();
    return response;
  });
}
